import React from 'react';
import { Link } from 'react-router-dom';
import './Facilities.css';

const Facilities: React.FC = () => {
  const facilities = [
    {
      code: 'F.01',
      name: 'TRANSFER HUB',
      title: '환적 허브 센터 (평택권)',
      description: '평택권 유통센터·디포 연계 환적 허브',
      subtitle: '대형 유통사 납품을 고려한 전략적 환적 거점',
      image: '/images/facility/facility_pyeongtaek.png',
      details: [
        '위치 — 수도권·중부권을 연결하는 핵심 물류 거점',
        '공간 — 대량 환적 대응 가능한 운영 공간',
        '프로세스 — 출고 스케줄 기반 환적·재배차 운영',
        '처리 역량 — 대량·긴급 물량 탄력 대응 가능'
      ],
      note: '천일은 유통센터·디포 납품대행 경험을 기반으로 현장 운영 기준에 맞춘 환적·납품 프로세스를 설계합니다.'
    },
    {
      code: 'F.02',
      name: 'WAREHOUSE',
      title: '보관창고 (WMS 기반)',
      description: '보관–재고–출고까지 연결되는 운영형 창고 시스템',
      image: '/images/facility/facility_storage.png',
      details: [
        '보관 방식 — PLT / BOX 단위 유연한 보관',
        'WMS 운영 — 실시간 재고·입출고 관리',
        '관리 항목 — LOT / 유통기한 / 출고 우선순위',
        '안전·방재 — 화물 안전을 고려한 관리 체계',
        '확장성 — 물량 증가 시 추가 공간 연계 가능'
      ],
      synergy: [
        '재고 정확도 향상',
        '유통기한·LOT 오류로 인한 납품 리스크 감소',
        '보세 → 내수 전환 시 이동·재작업 최소화'
      ]
    },
    {
      code: 'F.03',
      name: 'TRANSPORT',
      title: '차량 / 운송 인프라',
      description: '납품대행 중심으로 설계된 내국운송 네트워크',
      image: '/images/facility/facility_transport.png',
      details: [
        '차량 구성 — 1톤 ~ 5톤 (물량·조건별 배차)',
        '운영 범위 — 수도권 및 전국 터미널 연계',
        '전문 영역 — 대형 유통센터 납품 대응',
        '배송 형태 — B2B / B2C 출고 모두 지원'
      ],
      synergy: [
        '납품 스케줄 변경·긴급 대응 가능',
        '납품 실패 시 재배차·대체 운송 유연성 확보',
        '운송·납품 커뮤니케이션 단일화'
      ]
    },
    {
      code: 'F.04',
      name: 'WORKSHOP',
      title: '작업장 / 패킹룸',
      description: '유통사·수입요건을 동시에 고려한 전문 작업 공간',
      image: '/images/facility/facility_workshop.png',
      details: [
        '라벨링 전용 작업 구역',
        '포장·재포장 작업 공간',
        '세트 구성·프로모션 작업대',
        '입·출고 전 품질검수 구역'
      ],
      synergy: [
        '유통센터 반려·재작업 최소화',
        '수입요건·표시기준 미비로 인한 리스크 감소',
        '대량·반복 작업 안정적 대응'
      ]
    }
  ];

  const equipment = [
    { name: '지게차', description: '다양한 톤급 보유, 상·하차 효율 극대화' },
    { name: '파렛트', description: '표준 파렛트 및 특수 파렛트 대응' },
    { name: '컨베이어', description: '작업 효율을 높이는 내부 이송 설비' },
    { name: '운송 차량', description: '1~5톤급 자체·협력 차량 운영' },
    { name: '특수 지게차', description: '푸쉬풀, 레이어 피커 등 특수 작업 대응' }
  ];

  return (
    <div className="facilities-page">
      {/* =================================================================
          Hero — editorial
          ================================================================= */}
      <section
        className="facilities-hero"
        style={{ backgroundImage: "url(https://gallery.chunilkor.co.kr/ps/facilities-hero.jpg)" }}
      >
        <div className="container">
          <div className="hero-eyebrow">
            <span>01 / Facilities</span>
            <span className="hero-eyebrow-divider" />
            <span>Hub · Warehouse · Transport · Workshop</span>
          </div>
          <h1 className="hero-title">보관 / 센터 / 장비</h1>
          <p className="hero-lede">
            천일의 물류 인프라와 운영 거점을 통해, 밴더사의 납품·보관·출고 리스크를 구조적으로 줄입니다.
          </p>
          <div className="hero-cta">
            <Link to="/contact" className="btn-editorial-light">
              견적 요청하기
              <span aria-hidden className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* =================================================================
          Section 02 — Facility cards
          ================================================================= */}
      <section className="facilities-section facilities-list-section">
        <div className="container">
          <div className="section-header-split">
            <div className="section-header-left">
              <div className="eyebrow">02 / Infrastructure</div>
              <h2 className="section-title">운영 거점 & 시설</h2>
            </div>
            <div className="section-header-right">
              <p className="section-lede">
                각 시설은 독립 운영되며, 수입·보세·상품화·납품 전 단계를 하나의 흐름으로 연결합니다.
              </p>
            </div>
          </div>

          <div className="facilities-grid">
            {facilities.map((facility) => (
              <article key={facility.code} className="facility-card">
                <header className="facility-card-header">
                  <span className="facility-card-code">{facility.code}</span>
                  <span className="facility-card-divider" />
                  <span className="facility-card-name">{facility.name}</span>
                </header>

                <div className="facility-card-body">
                  <div className="facility-card-figure">
                    {facility.image ? (
                      <>
                        <img
                          src={facility.image}
                          alt={facility.title}
                          className="facility-card-image"
                          loading="lazy"
                        />
                        <span className="ai-label">AI생성</span>
                      </>
                    ) : (
                      <div className="image-placeholder">{facility.title}</div>
                    )}
                  </div>

                  <div className="facility-card-text">
                    <h3 className="facility-card-title">{facility.title}</h3>
                    <p className="facility-card-description">{facility.description}</p>
                    {facility.subtitle && (
                      <p className="facility-card-subtitle">{facility.subtitle}</p>
                    )}
                  </div>
                </div>

                <div className="facility-card-lists">
                  <div className="facility-card-list-group">
                    <div className="eyebrow">Details</div>
                    <ul className="facility-card-list">
                      {facility.details.map((detail, idx) => (
                        <li key={idx}>
                          <span className="list-index">{String(idx + 1).padStart(2, '0')}</span>
                          <span className="list-text">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {facility.synergy && (
                    <div className="facility-card-list-group">
                      <div className="eyebrow amber">Vendor Synergy</div>
                      <ul className="facility-card-list">
                        {facility.synergy.map((item, idx) => (
                          <li key={idx}>
                            <span className="list-index amber">{String(idx + 1).padStart(2, '0')}</span>
                            <span className="list-text">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {facility.note && (
                  <p className="facility-card-note">
                    <span className="facility-card-note-mark">※</span>
                    {facility.note}
                  </p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================================
          Section 03 — Equipment
          ================================================================= */}
      <section className="facilities-section equipment-section">
        <div className="container">
          <div className="section-header-split">
            <div className="section-header-left">
              <div className="eyebrow">03 / Equipment</div>
              <h2 className="section-title">주요 장비 및 설비</h2>
            </div>
            <div className="section-header-right">
              <p className="section-lede">
                작업 효율과 상·하차 속도를 높이는 핵심 장비 구성. 물량·화물 특성에 따라 유연하게 운용합니다.
              </p>
            </div>
          </div>

          <ol className="equipment-list">
            {equipment.map((item, idx) => (
              <li key={item.name} className="equipment-row">
                <span className="equipment-number">{String(idx + 1).padStart(2, '0')}</span>
                <div className="equipment-content">
                  <h3 className="equipment-name">{item.name}</h3>
                  <p className="equipment-description">{item.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* =================================================================
          Section 04 — Positioning (navy CTA)
          ================================================================= */}
      <section className="facilities-cta-section">
        <div className="container">
          <div className="cta-grid">
            <div className="cta-left">
              <div className="eyebrow light">04 / Positioning</div>
              <h2 className="cta-title">
                천일국제물류의 인프라는 <span className="accent-amber">'보관 공간'이 아니라,</span><br />
                <span className="accent-white">'납품 성공률을 높이기 위한 운영 자산'</span>입니다.
              </h2>
              <p className="cta-description">
                수입·보세·상품화·납품까지 하나의 흐름으로 연결해, 밴더사의 물류 운영 부담을 실질적으로 줄입니다.
              </p>
            </div>
            <div className="cta-right">
              <Link to="/contact" className="btn-editorial-light">
                시설 이용 문의
                <span aria-hidden className="arrow">→</span>
              </Link>
              <p className="cta-note">상담 시간: 평일 10:00 – 18:00</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Facilities;
